import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_CONSOLE_USERS } from "../queries/userQueries";
import Spinner from "../components/Spinner";
import NotFound from "./NotFound";
import ConsoleUserRow from "../components/ConsoleUserRow";
import { FaPlusCircle } from "react-icons/fa";
import CreateNewUserModal from "../components/modals/CreateNewUserModal";
import isAdministrator from "../utilities/checkPrivileges";
import directoryUserService from "../services/directoryUserService";
import locationService from "../services/locationService";
import { toast } from "react-toastify";

const ROLE_LABEL = {
  student: "Schüler",
  teacher: "Lehrer",
  staff: "Mitarbeiter",
};

const emptyDirectory = {
  name: "",
  firstName: "",
  lastName: "",
  email: "",
  username: "",
  password: "",
  role: "student",
  className: "",
  location: "",
  managedAppleId: "",
  asmUserId: "",
};

function Users() {
  const [consoleModal, setConsoleModal] = useState({ open: false, user: null });
  const [directoryUsers, setDirectoryUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dirForm, setDirForm] = useState(emptyDirectory);
  const [editingDirectoryId, setEditingDirectoryId] = useState(null);
  const [dirBusy, setDirBusy] = useState(false);
  const admin = isAdministrator();

  const { loading, error, data, refetch } = useQuery(GET_CONSOLE_USERS);

  const closeConsoleModal = async () => {
    setConsoleModal({ open: false, user: null });
    try {
      await refetch();
    } catch {
      /* ignore */
    }
  };

  const loadDirectoryUsers = async () => {
    try {
      setDirectoryUsers(await directoryUserService.listDirectoryUsers());
    } catch (err) {
      toast.error(err.response?.data?.message || "Gerätebenutzer konnten nicht geladen werden.");
    }
  };

  useEffect(() => {
    loadDirectoryUsers();
    locationService.getLocations().then(setLocations).catch(() => setLocations([]));
  }, []);

  const startEditDirectoryUser = (user) => {
    setEditingDirectoryId(user._id);
    setDirForm({
      name: user.name || "",
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      username: user.username || "",
      password: "",
      role: user.role || "student",
      className: user.className || "",
      location: user.location?._id || user.location || "",
      managedAppleId: user.managedAppleId || "",
      asmUserId: user.asmUserId || "",
    });
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const cancelDirectoryEdit = () => {
    setEditingDirectoryId(null);
    setDirForm(emptyDirectory);
  };

  const saveDirectoryUser = async (event) => {
    event.preventDefault();
    setDirBusy(true);
    try {
      const payload = {
        ...dirForm,
        location: dirForm.location || null,
      };
      if (editingDirectoryId) {
        if (!payload.password) delete payload.password;
        await directoryUserService.updateDirectoryUser(editingDirectoryId, payload);
        toast.success("Gerätebenutzer aktualisiert.");
        cancelDirectoryEdit();
      } else {
        await directoryUserService.createDirectoryUser(payload);
        toast.success("Gerätebenutzer angelegt. Dieser Account kann sich nur auf der Enrollment-Seite anmelden.");
        setDirForm(emptyDirectory);
      }
      await loadDirectoryUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || (editingDirectoryId ? "Aktualisieren fehlgeschlagen." : "Anlegen fehlgeschlagen."));
    } finally {
      setDirBusy(false);
    }
  };

  const removeDirectoryUser = async (id) => {
    if (!window.confirm("Gerätebenutzer wirklich löschen?")) return;
    try {
      await directoryUserService.deleteDirectoryUser(id);
      if (editingDirectoryId === id) cancelDirectoryEdit();
      await loadDirectoryUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Löschen fehlgeschlagen.");
    }
  };

  if (loading) return <Spinner />;
  if (error) return <NotFound />;

  const consoleUsers = data?.consoleusers || [];

  return (
    <main>
      <div className="header">
        <h1>Console Users</h1>
      </div>
      <p className="text-muted">Diese Konten steuern Xavier (Administrator / Auditor / Lehrer).</p>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>eMail</th>
            <th>Privileges</th>
            {admin && <th></th>}
          </tr>
        </thead>
        <tbody>
          {consoleUsers.map((consoleUser) => (
            <ConsoleUserRow
              key={consoleUser._id || consoleUser.email}
              consoleUser={consoleUser}
              onEdit={admin ? (user) => setConsoleModal({ open: true, user }) : undefined}
            />
          ))}
        </tbody>
      </table>
      {admin ? (
        <>
          {consoleModal.open ? (
            <CreateNewUserModal
              visible={consoleModal.open}
              user={consoleModal.user}
              hideCreateNewUserModal={closeConsoleModal}
            />
          ) : (
            <button className="btn" type="button" onClick={() => setConsoleModal({ open: true, user: null })}>
              <FaPlusCircle />
            </button>
          )}
        </>
      ) : null}

      <div className="header mt-5">
        <h1>Gerätebenutzer</h1>
      </div>
      <p className="text-muted">
        Kein MDM-Login. Zuweisung zum Gerät in Gerätedetails, Geräteliste und Registrierung.
        Klassen verwalten Sie unter Unterricht. Optional Managed Apple ID / ASM-User-ID für spätere Verknüpfung.
      </p>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Benutzername</th>
            <th>E-Mail</th>
            <th>Rolle</th>
            <th>Klasse / Gruppe</th>
            <th>Managed Apple ID</th>
            <th>ASM User-ID</th>
            <th>Standort</th>
            {admin && <th></th>}
          </tr>
        </thead>
        <tbody>
          {directoryUsers.map((user) => (
            <tr key={user._id}>
              <td>{user.name}</td>
              <td><code>{user.username}</code></td>
              <td>{user.email}</td>
              <td>{ROLE_LABEL[user.role] || user.role}</td>
              <td>{user.className || "—"}</td>
              <td>{user.managedAppleId || "—"}</td>
              <td>{user.asmUserId || "—"}</td>
              <td>{user.location?.name || user.location?.schoolNumber || "—"}</td>
              {admin && (
                <td className="text-nowrap">
                  <button type="button" className="btn btn-sm btn-outline-primary me-1" onClick={() => startEditDirectoryUser(user)}>
                    Bearbeiten
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeDirectoryUser(user._id)}>
                    Löschen
                  </button>
                </td>
              )}
            </tr>
          ))}
          {!directoryUsers.length && (
            <tr><td colSpan={admin ? 9 : 8} className="text-muted">Noch keine Gerätebenutzer.</td></tr>
          )}
        </tbody>
      </table>
      {admin && (
        <form className="card card-body" onSubmit={saveDirectoryUser}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <h2 className="h5 mb-0">
              {editingDirectoryId ? "Gerätebenutzer bearbeiten" : "Neuen Gerätebenutzer anlegen"}
            </h2>
            {editingDirectoryId ? (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelDirectoryEdit} disabled={dirBusy}>
                Abbrechen
              </button>
            ) : null}
          </div>
          <div className="row g-2">
            <div className="col-md-2">
              <label className="form-label">Vorname</label>
              <input className="form-control" value={dirForm.firstName} onChange={(e) => setDirForm({ ...dirForm, firstName: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Nachname</label>
              <input className="form-control" value={dirForm.lastName} onChange={(e) => setDirForm({ ...dirForm, lastName: e.target.value })} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Anzeigename</label>
              <input className="form-control" required value={dirForm.name} onChange={(e) => setDirForm({ ...dirForm, name: e.target.value })} />
            </div>
            <div className="col-md-4">
              <label className="form-label">E-Mail</label>
              <input className="form-control" type="email" required value={dirForm.email} onChange={(e) => setDirForm({ ...dirForm, email: e.target.value })} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Benutzername</label>
              <input className="form-control" required value={dirForm.username} onChange={(e) => setDirForm({ ...dirForm, username: e.target.value })} />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                Passwort{editingDirectoryId ? " (leer lassen = unverändert)" : ""}
              </label>
              <input
                className="form-control"
                type="password"
                required={!editingDirectoryId}
                value={dirForm.password}
                onChange={(e) => setDirForm({ ...dirForm, password: e.target.value })}
                autoComplete="new-password"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Rolle</label>
              <select className="form-select" value={dirForm.role} onChange={(e) => setDirForm({ ...dirForm, role: e.target.value })}>
                <option value="student">Schüler</option>
                <option value="teacher">Lehrer</option>
                <option value="staff">Mitarbeiter</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Klasse / Gruppe</label>
              <input className="form-control" value={dirForm.className} onChange={(e) => setDirForm({ ...dirForm, className: e.target.value })} placeholder="z. B. 7a" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Managed Apple ID</label>
              <input className="form-control" value={dirForm.managedAppleId} onChange={(e) => setDirForm({ ...dirForm, managedAppleId: e.target.value })} />
            </div>
            <div className="col-md-4">
              <label className="form-label">ASM User-ID</label>
              <input className="form-control" value={dirForm.asmUserId} onChange={(e) => setDirForm({ ...dirForm, asmUserId: e.target.value })} placeholder="optional" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Standort</label>
              <select className="form-select" value={dirForm.location} onChange={(e) => setDirForm({ ...dirForm, location: e.target.value })}>
                <option value="">—</option>
                {locations.map((location) => (
                  <option key={location._id} value={location._id}>{location.name || location.schoolNumber}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary mt-3" disabled={dirBusy}>
            {dirBusy ? "Speichere…" : editingDirectoryId ? "Änderungen speichern" : "Gerätebenutzer anlegen"}
          </button>
        </form>
      )}
    </main>
  );
}

export default Users;
